import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function InterpreterLogin() {
  const { login, logout } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== "interpreter") {
        await logout();
        toast.error("This login is for interpreters. Clients please use the client sign in.");
        return;
      }
      toast.success(`Welcome back, ${u.name}`);
      nav("/interpreter");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen gradient-login flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden" data-testid="interpreter-login-card">
        <div className="p-10 pb-8">
          <h1 className="font-sans text-4xl font-bold text-[#0a2540] tracking-tight">Interpreter Login</h1>
          <div className="h-1 w-14 bg-yellow-400 mt-3 rounded-full" />
          <p className="mt-5 text-slate-600 leading-relaxed">
            Access to material and tools available exclusively to Interpreta contract interpreters.
            Not an interpreter?{" "}
            <Link to="/login" className="text-teal-600 font-medium" data-testid="client-login-link">
              Log in as a client.
            </Link>
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="interpreter-login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-semibold text-[#0a2540]">Username or Email Address</Label>
              <Input id="email" type="email" required value={email}
                data-testid="interpreter-login-email-input"
                onChange={(e) => setEmail(e.target.value)} className="h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-semibold text-[#0a2540]">Password</Label>
              <Input id="password" type="password" required value={password}
                data-testid="interpreter-login-password-input"
                onChange={(e) => setPassword(e.target.value)} className="h-12" />
            </div>
          </form>
        </div>
        <div className="bg-[#0a2540] px-10 py-6 flex justify-end">
          <Button onClick={submit} disabled={busy}
            data-testid="interpreter-login-submit-btn"
            className="rounded-full bg-yellow-400 text-[#0a2540] hover:bg-yellow-300 font-semibold h-12 px-10">
            {busy ? "Logging in…" : "Log In"}
          </Button>
        </div>
      </div>

      <div className="mt-8 text-center space-y-3 text-white/90">
        <div>
          Don't have an account?{" "}
          <Link to="/contact" className="font-semibold inline-flex items-center gap-1 hover:text-white" data-testid="contact-us-link">
            Contact us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white" data-testid="back-home-link">
          <ArrowLeft className="h-4 w-4" /> Back to Main Interpreta Website
        </Link>
      </div>
    </div>
  );
}
